import React from "react";
import { Link } from "@tanstack/react-router";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Printer,
  Layers,
  Box,
  User,
  Clock,
  ExternalLink,
  MessageSquare,
  Paperclip,
  History,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Edit2,
  XCircle,
} from "lucide-react";
import { formatNumber, formatDate } from "@/lib/utils";
import RelatedDataTabs from "@/components/shared/RelatedDataTabs";
import { EntityLink } from "@/components/shared/EntityLink";
import CommentSection from "@/components/shared/CommentSection";
import AttachmentSection from "@/components/shared/AttachmentSection";
import ActivityLogSection from "@/components/shared/ActivityLogSection";

export interface WipTransferDetailDrawerProps {
  transfer: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPrint?: (transfer: any) => void;
  onCancel?: (transfer: any) => void;
  onEdit?: (transfer: any) => void;
}

export function WipTransferDetailDrawer({
  transfer,
  open,
  onOpenChange,
  onPrint,
  onCancel,
  onEdit,
}: WipTransferDetailDrawerProps) {
  if (!transfer) return null;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "RECEIVED":
        return (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Đã nhận
          </Badge>
        );
      case "REJECTED":
        return (
          <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Từ chối
          </Badge>
        );
      case "PENDING":
      default:
        return (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Đang chuyển
          </Badge>
        );
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-6 flex flex-col justify-between z-[10000] overflow-y-auto">
        <div className="space-y-6">
          {/* Header */}
          <SheetHeader className="p-0 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="p-2 bg-indigo-50 text-indigo-700 rounded-lg">
                  <Layers className="w-5 h-5" />
                </span>
                <div>
                  <div className="flex items-center gap-2">
                    <SheetTitle className="text-lg font-bold text-slate-900 font-mono">
                      {transfer.transferId || transfer.id}
                    </SheetTitle>
                    {getStatusBadge(transfer.status)}
                  </div>
                  <SheetDescription className="text-xs text-slate-500">
                    Phiếu điều chuyển Bán Thành Phẩm (BTP) sàn xưởng
                  </SheetDescription>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {onEdit && transfer.status === 'PENDING' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs gap-1.5 border-slate-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                    onClick={() => onEdit(transfer)}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Sửa
                  </Button>
                )}
                {onCancel && transfer.status === 'PENDING' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => onCancel(transfer)}
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Hủy Phiếu
                  </Button>
                )}
                {onPrint && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs gap-1.5 border-slate-200 text-slate-700 hover:bg-slate-50"
                    onClick={() => onPrint(transfer)}
                  >
                    <Printer className="w-3.5 h-3.5" />
                    In thẻ
                  </Button>
                )}
              </div>
            </div>
          </SheetHeader>

          {/* Journey Card (From Step -> To Step) */}
          <div className="bg-gradient-to-r from-indigo-50/70 to-blue-50/70 border border-indigo-100 rounded-xl p-4">
            <div className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <ArrowRight className="w-3.5 h-3.5" /> Tuyến Điều Chuyển Công Đoạn
            </div>
            <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-indigo-100/80 shadow-sm">
              <div className="text-center flex-1">
                <div className="text-[10px] text-slate-400 uppercase font-semibold">Công đoạn xuất</div>
                <div className="font-bold text-slate-900 text-sm mt-0.5">{transfer.fromStep || "—"}</div>
              </div>
              <div className="px-3 flex items-center justify-center text-indigo-600">
                <ArrowRight className="w-5 h-5 animate-pulse" />
              </div>
              <div className="text-center flex-1">
                <div className="text-[10px] text-slate-400 uppercase font-semibold">Công đoạn nhận</div>
                <div className="font-bold text-slate-900 text-sm mt-0.5">{transfer.toStep || "—"}</div>
              </div>
            </div>
          </div>

          {/* Product & PO Info */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-4 space-y-3 text-xs">
            <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
              <span className="text-slate-500 flex items-center gap-1.5 font-medium">
                <FileText className="w-3.5 h-3.5 text-slate-400" /> Lệnh Sản Xuất
              </span>
              <span className="font-mono font-bold text-indigo-600">
                {transfer.productionOrderId ? (
                  <EntityLink
                    entityType="productionOrder"
                    id={transfer.productionOrderId}
                    code={transfer.poCode || transfer.productionOrderId}
                  />
                ) : (
                  "—"
                )}
              </span>
            </div>

            <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
              <span className="text-slate-500 flex items-center gap-1.5 font-medium">
                <Box className="w-3.5 h-3.5 text-slate-400" /> Tên Sản Phẩm
              </span>
              <span className="font-semibold text-slate-900 text-right max-w-[280px] break-words whitespace-normal">
                {transfer.productId ? (
                  <EntityLink
                    entityType="product"
                    id={transfer.productId}
                    label={transfer.productName || "—"}
                  />
                ) : (
                  transfer.productName || "—"
                )}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-slate-500 flex items-center gap-1.5 font-medium">
                <Layers className="w-3.5 h-3.5 text-slate-400" /> Mã Pallet / Thẻ Kho
              </span>
              <Badge variant="outline" className="font-mono font-bold bg-white text-slate-800 border-slate-200">
                {transfer.palletCode || "—"}
              </Badge>
            </div>
          </div>

          {/* Quantities Grid */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
              <div className="text-[10px] text-slate-500 font-semibold uppercase">SL Bàn Giao</div>
              <div className="text-base font-bold font-mono text-slate-900 mt-1">
                {formatNumber(transfer.quantity || 0)}
              </div>
            </div>
            <div className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-3 text-center">
              <div className="text-[10px] text-emerald-700 font-semibold uppercase">SL Thực Nhận</div>
              <div className="text-base font-bold font-mono text-emerald-700 mt-1">
                {formatNumber(transfer.receivedQty || 0)}
              </div>
            </div>
            <div className="bg-rose-50/60 border border-rose-200 rounded-lg p-3 text-center">
              <div className="text-[10px] text-rose-700 font-semibold uppercase">SL Lỗi / Bù Hao</div>
              <div className="text-base font-bold font-mono text-rose-700 mt-1">
                {formatNumber(transfer.rejectedQty || 0)}
              </div>
            </div>
          </div>

          {/* Transfer & Receive Actors */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="border border-slate-200/80 rounded-lg p-3 space-y-1.5 bg-white">
              <div className="flex items-center gap-1.5 font-bold text-slate-700">
                <User className="w-3.5 h-3.5 text-indigo-500" /> Người giao BTP
              </div>
              <div className="text-slate-900 font-medium">{transfer.senderName || "—"}</div>
              <div className="text-[11px] text-slate-500 flex items-center gap-1">
                <Clock className="w-3 h-3 text-slate-400" />
                {transfer.createdAt ? formatDate(transfer.createdAt) : "—"}
              </div>
            </div>

            <div className="border border-slate-200/80 rounded-lg p-3 space-y-1.5 bg-white">
              <div className="flex items-center gap-1.5 font-bold text-slate-700">
                <User className="w-3.5 h-3.5 text-emerald-500" /> Người nhận BTP
              </div>
              <div className="text-slate-900 font-medium">{transfer.receiverName || "Chưa tiếp nhận"}</div>
              <div className="text-[11px] text-slate-500 flex items-center gap-1">
                <Clock className="w-3 h-3 text-slate-400" />
                {transfer.receivedAt ? formatDate(transfer.receivedAt) : "—"}
              </div>
            </div>
          </div>

          {/* Notes if any */}
          {transfer.notes && (
            <div className="p-3 bg-amber-50/60 border border-amber-200/80 rounded-lg text-amber-900 text-xs">
              <strong>Ghi chú bàn giao:</strong> {transfer.notes}
            </div>
          )}

          {/* Context Tabs (Comments, Attachments, Audit Logs) */}
          <div className="pt-2 border-t border-slate-200">
            <RelatedDataTabs
              tabs={[
                {
                  id: "comments",
                  label: "Thảo luận & Biên bản",
                  icon: <MessageSquare className="w-4 h-4 text-blue-500" />,
                  content: (
                    <div className="p-4">
                      <CommentSection entityType="wip_transfer" entityId={transfer.id} />
                    </div>
                  ),
                },
                {
                  id: "attachments",
                  label: "Ảnh kiện hàng & Tài liệu",
                  icon: <Paperclip className="w-4 h-4 text-emerald-500" />,
                  content: (
                    <div className="p-4">
                      <AttachmentSection entityType="wip_transfer" entityId={transfer.id} />
                    </div>
                  ),
                },
                {
                  id: "history",
                  label: "Lịch sử luân chuyển",
                  icon: <History className="w-4 h-4 text-slate-500" />,
                  content: (
                    <div className="p-4">
                      <ActivityLogSection entityType="wip_transfer" entityId={transfer.id} />
                    </div>
                  ),
                },
              ]}
              defaultTab="comments"
            />
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200 mt-6">
          <Button
            variant="outline"
            size="sm"
            className="h-9 px-4 text-xs font-medium text-slate-700"
            onClick={() => onOpenChange(false)}
          >
            Đóng
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default WipTransferDetailDrawer;
